/**
 * OpenStreetMap Service using Overpass API
 * HMM-based map matching for GPS tracking
 * Uses non-Russian servers only
 */

class OSMService {
  constructor() {
    // Use only non-Russian Overpass API servers
    this.servers = [
      'https://overpass-api.de/api/interpreter',      // Germany (primary)
      'https://lz4.overpass-api.de/api/interpreter',  // Germany (backup)
      'https://z.overpass-api.de/api/interpreter'     // Germany (backup)
    ];
    this.currentServerIndex = 0;
    this.cache = new Map();
    this.cacheTimeout = 10 * 60 * 1000; // 10 minutes
    this.requestDelay = 1000; // 1 second between requests (rate limiting)
    this.lastRequestTime = 0;
  }

  /**
   * Get road network data for HMM map matching
   * @param {Object} bounds - {minLat, maxLat, minLon, maxLon}
   * @param {Object} options - Configuration options
   */
  async getRoadNetwork(bounds, options = {}) {
    const {
      includeRoads = true,
      includeFootways = true,
      includePaths = true,
      includeSteps = true,
      maxDistance = 200 // meters from bounds
    } = options;

    const cacheKey = this.getCacheKey(bounds, options);
    
    // Check cache
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    try {
      // Rate limiting
      await this.enforceRateLimit();
      
      const query = this.buildHMMQuery(bounds, options);
      const data = await this.executeQuery(query);
      const roadNetwork = this.parseRoadNetwork(data);
      
      // Cache results
      this.cache.set(cacheKey, {
        data: roadNetwork,
        timestamp: Date.now()
      });

      return roadNetwork;
    } catch (error) {
      console.error('OSM query failed:', error);
      // Try next server if available
      if (this.currentServerIndex < this.servers.length - 1) {
        this.currentServerIndex++;
        return this.getRoadNetwork(bounds, options);
      }
      return [];
    }
  }

  /**
   * Build optimized query for HMM map matching
   */
  buildHMMQuery(bounds, options) {
    const { minLat, maxLat, minLon, maxLon } = bounds;
    const bbox = `${minLat},${minLon},${maxLat},${maxLon}`;
    
    // Build highway type filter for HMM
    const highwayTypes = [];
    
    if (options.includeRoads) {
      highwayTypes.push('"highway"~"^(primary|secondary|tertiary|residential|unclassified|service|living_street)$"');
    }
    
    if (options.includeFootways) {
      highwayTypes.push('"highway"~"^(footway|pedestrian)$"');
    }
    
    if (options.includePaths) {
      highwayTypes.push('"highway"~"^(path|track|bridleway)$"');
    }
    
    if (options.includeSteps) {
      highwayTypes.push('"highway"="steps"');
    }

    const highwayFilter = highwayTypes.join('|');
    
    // Optimized query for HMM - get nodes and ways
    return `
      [out:json][timeout:30];
      (
        way[${highwayFilter}](${bbox});
        node(w)["highway"~"^(primary|secondary|tertiary|residential|unclassified|service|living_street|footway|pedestrian|path|track|bridleway|steps)$"](${bbox});
      );
      out geom;
    `;
  }

  /**
   * Execute Overpass API query with error handling
   */
  async executeQuery(query) {
    const server = this.servers[this.currentServerIndex];
    
    const response = await fetch(server, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `data=${encodeURIComponent(query)}`
    });

    if (!response.ok) {
      throw new Error(`Overpass API error: ${response.status} from ${server}`);
    }

    return await response.json();
  }

  /**
   * Parse OSM data into road network for HMM
   */
  parseRoadNetwork(data) {
    if (!data.elements) return { nodes: [], ways: [] };

    const nodes = new Map();
    const ways = [];

    // Parse nodes first
    data.elements
      .filter(element => element.type === 'node')
      .forEach(node => {
        nodes.set(node.id, {
          id: node.id,
          latitude: node.lat,
          longitude: node.lon,
          type: node.tags?.highway || 'unknown',
          properties: node.tags || {}
        });
      });

    // Parse ways
    data.elements
      .filter(element => element.type === 'way' && element.geometry)
      .forEach(way => {
        const roadWay = {
          id: way.id,
          type: way.tags?.highway || 'unknown',
          name: way.tags?.name || `Road ${way.id}`,
          nodes: way.nodes || [],
          geometry: way.geometry.map(node => ({
            latitude: node.lat,
            longitude: node.lon
          })),
          properties: {
            surface: way.tags?.surface,
            oneway: way.tags?.oneway === 'yes',
            maxspeed: way.tags?.maxspeed,
            foot: way.tags?.foot,
            bicycle: way.tags?.bicycle,
            motor_vehicle: way.tags?.motor_vehicle,
            access: way.tags?.access,
            lanes: way.tags?.lanes
          },
          isWalkable: this.isWalkable(way.tags),
          length: this.calculateWayLength(way.geometry)
        };

        ways.push(roadWay);
      });

    return { nodes, ways };
  }

  /**
   * Determine if a way is walkable
   */
  isWalkable(tags) {
    const highway = tags?.highway;
    const access = tags?.access;
    const foot = tags?.foot;

    // Check access restrictions
    if (access === 'private' || access === 'no') return false;
    if (foot === 'no') return false;

    // Check highway type
    const walkableTypes = [
      'footway', 'path', 'pedestrian', 'steps', 'bridleway',
      'primary', 'secondary', 'tertiary', 'residential', 
      'unclassified', 'service', 'living_street', 'track'
    ];

    return walkableTypes.includes(highway);
  }

  /**
   * Calculate way length in meters
   */
  calculateWayLength(geometry) {
    if (!geometry || geometry.length < 2) return 0;

    let length = 0;
    for (let i = 1; i < geometry.length; i++) {
      length += this.calculateDistance(
        geometry[i-1].lat, geometry[i-1].lon,
        geometry[i].lat, geometry[i].lon
      );
    }
    return length;
  }

  /**
   * Calculate distance between two points in meters
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Enforce rate limiting
   */
  async enforceRateLimit() {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.requestDelay) {
      await new Promise(resolve => 
        setTimeout(resolve, this.requestDelay - timeSinceLastRequest)
      );
    }
    
    this.lastRequestTime = Date.now();
  }

  /**
   * Get cache key
   */
  getCacheKey(bounds, options) {
    return `${bounds.minLat},${bounds.minLon},${bounds.maxLat},${bounds.maxLon}-${JSON.stringify(options)}`;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      currentServer: this.servers[this.currentServerIndex]
    };
  }
}

export default new OSMService();
